import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/apiClient'
import type {
  DunningRule,
  DunningPreviewRow,
  RemindCustomerBody,
} from '@/types/backend'

export const useDunningRulesQuery = (enabled = true) =>
  useQuery({
    queryKey: ['dunning-rules'],
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<DunningRule[]>('/dunning/rules')
      return data
    },
  })

export const useCreateDunningRuleMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      payload: Partial<DunningRule> & {
        name: string
        channel: 'EMAIL' | 'WHATSAPP' | 'SMS'
      }
    ) => {
      const { data } = await apiClient.post<DunningRule>(
        '/dunning/rules',
        payload
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dunning-rules'] }),
  })
}

export const useUpdateDunningRuleMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<DunningRule>) => {
      const { data } = await apiClient.patch<DunningRule>(
        `/dunning/rules/${id}`,
        payload
      )
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dunning-rules'] }),
  })
}

export const useDeleteDunningRuleMutation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete(`/dunning/rules/${id}`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dunning-rules'] }),
  })
}

export const usePreviewRuleQuery = (id?: string) =>
  useQuery({
    queryKey: ['dunning-rules', id, 'preview'],
    enabled: !!id,
    queryFn: async () => {
      const { data } = await apiClient.get<DunningPreviewRow[]>(
        `/dunning/rules/${id}/preview`
      )
      return data
    },
  })

export const useRunRuleNowMutation = (id: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(`/dunning/rules/${id}/run-now`, {})
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dunning-rules'] })
    },
  })
}

export const useRemindCustomerMutation = () =>
  useMutation({
    mutationFn: async (payload: RemindCustomerBody) => {
      const { data } = await apiClient.post(`/dunning/remind-customer`, payload)
      return data
    },
  })
